import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Box, Switch, FormControlLabel,
    Tabs, Tab, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DomainIcon from '@mui/icons-material/Domain';
import { cadastrarGrupoEconomico, atualizarGrupoEconomico, listarEmpresas } from '../../services/api-empresas.service';
import BlueButton from '../BlueButton';

const resolveGrupoId = (grupo) =>
    grupo?.idGrupoEconomico ?? grupo?.id_grupo_economico ?? grupo?.id;

const ModalCadastroGrupoEconomico = ({
    open,
    onClose,
    onSuccess,
    initialData,
    readOnly = false,
}) => {
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        nome: '',
        ativo: true,
    });

    // Estado para lista de empresas (Aba 2)
    const [empresas, setEmpresas] = useState([]);
    const [loadingEmpresas, setLoadingEmpresas] = useState(false);

    useEffect(() => {
        if (open) {
            setTabIndex(0); // Sempre abrir na aba de Dados
            if (initialData) {
                setFormData({
                    nome: initialData.nome || '',
                    ativo: initialData.ativo !== undefined ? initialData.ativo : true,
                });
                // Carregar empresas se estiver editando
                loadEmpresasdoGrupo(resolveGrupoId(initialData));
            } else {
                setFormData({
                    nome: '',
                    ativo: true,
                });
                setEmpresas([]);
            }
        }
    }, [open, initialData]);

    const loadEmpresasdoGrupo = async (idGrupo) => {
        if (!idGrupo) return;
        setLoadingEmpresas(true);
        try {
            const response = await listarEmpresas({ id_grupo_economico: idGrupo, limit: 500, offset: 0 });
            const lista = response?.dados != null ? response.dados : (Array.isArray(response) ? response : []);
            setEmpresas(lista);
        } catch (error) {
            console.error("Erro ao carregar empresas do grupo", error);
        } finally {
            setLoadingEmpresas(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        if (readOnly) return;
        if (!formData.nome.trim()) {
            alert('Nome do grupo é obrigatório');
            return;
        }

        const isEdit = Boolean(initialData);
        const grupoId = isEdit ? resolveGrupoId(initialData) : null;

        if (isEdit && !grupoId) {
            alert('Não foi possível identificar o grupo econômico para edição.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                grupoEconomico: {
                    nome: formData.nome.trim(),
                    ativo: formData.ativo,
                },
            };

            if (isEdit) {
                await atualizarGrupoEconomico(grupoId, payload);
            } else {
                await cadastrarGrupoEconomico(payload);
            }

            onSuccess();
            onClose();
        } catch (error) {
            console.error(error);
            const apiMessage =
                error.response?.data?.mensagem ||
                error.response?.data?.message ||
                error.response?.data?.erro;
            alert(
                'Erro ao salvar grupo econômico: ' +
                (apiMessage || error.message)
            );
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ bgcolor: '#0d47a1', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon />{" "}
                {readOnly
                    ? "Visualizar Grupo Econômico"
                    : initialData
                      ? "Editar Grupo Econômico"
                      : "Novo Grupo Econômico"}
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: '#f5f5f5' }}>
                    <Tabs value={tabIndex} onChange={handleTabChange} aria-label="tabs grupo economico">
                        <Tab icon={<ListAltIcon />} iconPosition="start" label="Dados Gerais" />
                        <Tab
                            icon={<DomainIcon />}
                            iconPosition="start"
                            label={`Empresas Vinculadas (${empresas.length})`}
                            disabled={!initialData}
                        />
                    </Tabs>
                </Box>

                {/* ABA 1: DADOS GERAIS */}
                <Box role="tabpanel" hidden={tabIndex !== 0} sx={{ p: 3 }}>
                    {tabIndex === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Nome do Grupo Econômico"
                                name="nome"
                                value={formData.nome}
                                onChange={handleChange}
                                fullWidth
                                required
                                margin="normal"
                                disabled={readOnly}
                                InputProps={{ readOnly }}
                                inputProps={{ "data-allow-numeric": "true" }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.ativo}
                                        onChange={handleChange}
                                        name="ativo"
                                        color="primary"
                                        disabled={readOnly}
                                    />
                                }
                                label={formData.ativo ? "Ativo" : "Inativo"}
                            />
                            {readOnly && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Para alterar dados ou status, use Editar. A desativação é feita pelo interruptor Ativo/Inativo.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {/* ABA 2: EMPRESAS VINCULADAS */}
                <Box role="tabpanel" hidden={tabIndex !== 1} sx={{ p: 0 }}>
                    {tabIndex === 1 && (
                        <>
                            {loadingEmpresas ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>CNPJ</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Razão Social</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {empresas.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                        Nenhuma empresa vinculada a este grupo.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                empresas.map((emp) => (
                                                    <TableRow key={emp.id}>
                                                        <TableCell>{emp.cnpj}</TableCell>
                                                        <TableCell>{emp.razao_social}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={emp.ativo ? "Ativa" : "Inativa"}
                                                                size="small"
                                                                color={emp.ativo ? "success" : "default"}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">
                    {readOnly ? "Fechar" : "Cancelar"}
                </Button>
                {!readOnly && (
                    <BlueButton
                        textValue={loading ? "Salvando..." : "Salvar"}
                        clickFunction={handleSave}
                        disabled={loading}
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ModalCadastroGrupoEconomico;
